import { useMutation } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { createLogger } from '~/utils/logging';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { ClientError } from '~/errors';

import { type Timeframe } from './useStudentGenerateMessageMutation';
import { timeframeToApiParams } from './studentDataParams';

const log = createLogger('useStudentSummaryMutation');

export type StudentSummaryRequest = {
  studentId: string;
  periodId: string;
  periodCreatedAt: string;
  timeframe: Timeframe;
};

type StudentSummaryResponse = {
  summary: string;
  sessions: number;
  praises: number;
  warnings: number;
  absences: number;
};

export function useStudentSummaryMutation() {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, error, mutate, status } = useMutation({
    mutationFn: async (request: StudentSummaryRequest) => {
      const tz = locale?.timeZone ?? '';
      const today = new Date().toISOString().slice(0, 10);
      const dateParams = timeframeToApiParams(request.timeframe, request.periodCreatedAt);

      log.debug('Generating student summary', {
        studentId: request.studentId,
        periodId: request.periodId,
        timeframe: request.timeframe,
      });

      const requestBody = {
        studentId: request.studentId,
        periodId: request.periodId,
        tz,
        ...('range' in dateParams ? { day: today } : {}),
        include: ['praises', 'warnings', 'attendance'],
        ...dateParams,
      };

      log.debug('Request body', { requestBody });

      const result = await httpClient.post<StudentSummaryResponse>('/report/generateStudentSummary', requestBody);

      log.info('Generated student summary successfully', { studentId: request.studentId, result: result });
      return result;
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to generate student summary', { errorMessage: sanitizeTagValue(error.message) });
      }
    },
  });

  return { data, error, mutate, status };
}
